/**
 * Catálogo de widgets del dashboard.
 *
 * Cada entrada describe un widget que el usuario puede poner en su dashboard:
 *  - `type`: clave guardada en el layout (`UserDashboardLayout.widgets[].type`).
 *  - `kind`: qué componente lo dibuja (ver DashboardWidgetGrid). Las entradas
 *    `builtin` son los bloques de siempre del dashboard (calendario, resumen...) y
 *    los pinta Dashboard.vue por slot, con sus datos de siempre.
 *  - `preview`: qué miniatura se muestra en el popup (WidgetPreview).
 *  - `size` / `min`: tamaño inicial y mínimo en celdas de la grilla (12 columnas).
 *  - `available(ctx)`: si el tenant tiene el módulo y el usuario el permiso.
 *  - `multiple`: se puede agregar más de una vez (notas).
 *
 * Los reportes guardados entran uno por uno (`report:<id>`) y los query reports
 * (`view:<id>`) también; se arman con `buildDynamicEntries`.
 */

export const GRID_COLS = 12;
export const ROW_HEIGHT = 30;
export const GRID_MARGIN = 12;

export const WIDGET_CATEGORIES = ["general", "ai", "finance", "products", "contacts", "reports", "containers", "tracking", "support", "tools", "market"];

const BUILTIN_ENTRIES = [
  {
    type: "notifications",
    kind: "notifications",
    category: "general",
    icon: "bell",
    preview: "notifications",
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: () => true,
  },
  // Dudas que le hacemos al cliente sobre lo que desarrollamos: las ve todo el tenant, pero
  // solo si el tenant tiene dudas cargadas (se activa tenant por tenant, cargándolas) y los
  // widgets IA prendidos en Funciones del sistema (si no, el resumen dice total 0). Si hay
  // pendientes, la grilla lo pone sola arriba (DashboardWidgetGrid.loadClientQuestions).
  {
    type: "client-questions",
    kind: "client-questions",
    category: "ai",
    icon: "question-circle",
    preview: "list",
    size: { w: 6, h: 12 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.hasClientQuestions,
  },
  {
    type: "shortcuts",
    kind: "builtin",
    category: "general",
    icon: "gavel",
    preview: "shortcuts",
    size: { w: 12, h: 5 },
    min: { w: 4, h: 4 },
    available: (ctx) => ctx.showLegalShortcuts,
  },
  {
    type: "calendar",
    kind: "builtin",
    category: "general",
    icon: "calendar-alt",
    preview: "calendar",
    size: { w: 8, h: 21 },
    min: { w: 5, h: 12 },
    available: () => true,
  },
  {
    type: "recent-activity",
    kind: "builtin",
    category: "general",
    icon: "history",
    preview: "activity",
    size: { w: 4, h: 21 },
    min: { w: 3, h: 6 },
    available: () => true,
  },
  {
    type: "operations-summary",
    kind: "builtin",
    category: "general",
    icon: "th-large",
    preview: "summary",
    size: { w: 12, h: 12 },
    min: { w: 4, h: 6 },
    available: () => true,
  },
  {
    type: "finance:month",
    kind: "finance",
    category: "finance",
    icon: "calendar-check",
    preview: "kpi",
    config: { view: "month" },
    size: { w: 6, h: 6 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:cashflow",
    kind: "finance",
    category: "finance",
    icon: "exchange-alt",
    preview: "columns",
    config: { view: "cashflow" },
    size: { w: 6, h: 9 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:payments-pending",
    kind: "finance",
    category: "finance",
    icon: "file-invoice-dollar",
    preview: "alerts",
    config: { view: "payments-pending" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinancePayments,
  },
  {
    type: "finance:receivables",
    kind: "finance",
    category: "finance",
    icon: "hand-holding-usd",
    preview: "list",
    config: { view: "receivables" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:payables",
    kind: "finance",
    category: "finance",
    icon: "truck-loading",
    preview: "kpi",
    config: { view: "payables" },
    size: { w: 4, h: 5 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:bank-balances",
    kind: "finance",
    category: "finance",
    icon: "university",
    preview: "list",
    config: { view: "bank-balances" },
    size: { w: 4, h: 9 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:margins",
    kind: "finance",
    category: "finance",
    icon: "percentage",
    preview: "columns",
    config: { view: "margins", basis: "proforms" },
    size: { w: 6, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:margins-real",
    kind: "finance",
    category: "finance",
    icon: "chart-line",
    preview: "columns",
    config: { view: "margins", basis: "shippings" },
    size: { w: 6, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:low-margins",
    kind: "finance",
    category: "finance",
    icon: "arrow-down",
    preview: "alerts",
    config: { view: "low-margins", basis: "proforms" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  {
    type: "finance:advances",
    kind: "finance",
    category: "finance",
    icon: "coins",
    preview: "list",
    config: { view: "advances" },
    size: { w: 4, h: 9 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessFinance,
  },
  // Productos y contactos: lo que dicen las operaciones (TradeWidget, /api/trade-insights).
  {
    type: "products:summary",
    kind: "trade",
    category: "products",
    icon: "boxes",
    preview: "kpi",
    config: { view: "productsSummary" },
    size: { w: 6, h: 6 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.hasPermission("product.view"),
  },
  {
    type: "products:top",
    kind: "trade",
    category: "products",
    icon: "trophy",
    preview: "bars",
    config: { view: "productsTop", chart: "bar" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.hasPermission("product.view"),
  },
  {
    type: "products:by-month",
    kind: "trade",
    category: "products",
    icon: "weight-hanging",
    preview: "columns",
    config: { view: "productsByMonth", chart: "column" },
    size: { w: 6, h: 9 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.hasPermission("product.view"),
  },
  {
    type: "products:new",
    kind: "trade",
    category: "products",
    icon: "seedling",
    preview: "list",
    config: { view: "newProducts" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.hasPermission("product.view"),
  },
  {
    type: "contacts:top-clients",
    kind: "trade",
    category: "contacts",
    icon: "user-tie",
    preview: "bars",
    config: { view: "topClients", chart: "bar" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.hasPermission("contact.view"),
  },
  {
    type: "contacts:top-suppliers",
    kind: "trade",
    category: "contacts",
    icon: "industry",
    preview: "bars",
    config: { view: "topSuppliers", chart: "bar" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.hasPermission("contact.view"),
  },
  {
    type: "contacts:new-clients",
    kind: "trade",
    category: "contacts",
    icon: "user-plus",
    preview: "list",
    config: { view: "newClients" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.hasPermission("contact.view"),
  },
  {
    type: "contacts:dormant",
    kind: "trade",
    category: "contacts",
    icon: "user-clock",
    preview: "list",
    config: { view: "dormant" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.hasPermission("contact.view"),
  },
  {
    type: "contacts:concentration",
    kind: "trade",
    category: "contacts",
    icon: "chart-pie",
    preview: "bars",
    config: { view: "concentration", chart: "donut" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 7 },
    available: (ctx) => ctx.hasPermission("contact.view"),
  },
  {
    type: "containers:month",
    kind: "containers",
    category: "containers",
    icon: "ship",
    preview: "columns",
    config: { groupBy: "month" },
    size: { w: 6, h: 10 },
    min: { w: 3, h: 7 },
    available: (ctx) => ctx.canAccessStock,
  },
  {
    type: "containers:destination",
    kind: "containers",
    category: "containers",
    icon: "globe-americas",
    preview: "bars",
    config: { groupBy: "destination" },
    size: { w: 3, h: 10 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.canAccessStock,
  },
  {
    type: "containers:client",
    kind: "containers",
    category: "containers",
    icon: "user-tie",
    preview: "bars",
    config: { groupBy: "client" },
    size: { w: 3, h: 10 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.canAccessStock,
  },
  {
    type: "containers:shipper",
    kind: "containers",
    category: "containers",
    icon: "industry",
    preview: "bars",
    config: { groupBy: "shipper" },
    size: { w: 3, h: 10 },
    min: { w: 3, h: 6 },
    available: (ctx) => ctx.canAccessStock,
  },
  {
    type: "stock-health",
    kind: "stock-health",
    category: "containers",
    icon: "boxes",
    preview: "kpi",
    size: { w: 4, h: 5 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessStock,
  },
  {
    type: "tracking:status",
    kind: "tracking",
    category: "tracking",
    icon: "satellite-dish",
    preview: "kpi",
    config: { view: "status" },
    size: { w: 4, h: 6 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessTracking,
  },
  {
    type: "tracking:alerts",
    kind: "tracking",
    category: "tracking",
    icon: "exclamation-triangle",
    preview: "alerts",
    config: { view: "alerts" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessTracking,
  },
  {
    type: "tracking:arrivals",
    kind: "tracking",
    category: "tracking",
    icon: "anchor",
    preview: "arrivals",
    config: { view: "arrivals" },
    size: { w: 4, h: 10 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessTracking,
  },
  {
    type: "tracking:routes",
    kind: "tracking",
    category: "tracking",
    icon: "route",
    preview: "bars",
    config: { view: "routes" },
    size: { w: 4, h: 8 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessTracking,
  },
  {
    type: "support-tickets",
    kind: "support",
    category: "support",
    icon: "life-ring",
    preview: "kpi",
    size: { w: 4, h: 5 },
    min: { w: 3, h: 5 },
    available: (ctx) => ctx.canAccessSupport,
  },
  {
    type: "note",
    kind: "note",
    category: "tools",
    icon: "sticky-note",
    preview: "note",
    multiple: true,
    size: { w: 3, h: 7 },
    min: { w: 2, h: 4 },
    available: () => true,
  },
];

/**
 * Reportes guardados del menú (Report) y query reports (View type=query_report).
 * Un reporte solo aparece si el usuario puede ver su modelo; los query reports leen
 * colecciones crudas sin pasar por permisos, así que quedan para el admin.
 */
export function buildDynamicEntries({ reports = [], queryViews = [], ctx }) {
  const entries = [];
  reports.forEach((report) => {
    if (!report || !report._id || !report.modelName) return;
    entries.push({
      type: `report:${report._id}`,
      kind: "report",
      category: "reports",
      icon: report.icon || "chart-bar",
      preview: "list",
      title: report.name,
      description: report.description || "",
      modelName: report.modelName,
      report,
      config: { reportId: String(report._id), mode: "list" },
      size: { w: 4, h: 10 },
      min: { w: 3, h: 5 },
      available: (c) => c.isAdmin || c.hasPermission(`${report.modelName}.view`),
    });
  });
  queryViews.forEach((view) => {
    if (!view || !view._id) return;
    entries.push({
      type: `view:${view._id}`,
      kind: "report",
      category: "reports",
      icon: "database",
      preview: "list",
      title: view.name,
      description: view.description || "",
      view,
      config: { viewId: String(view._id), mode: "list" },
      size: { w: 4, h: 10 },
      min: { w: 3, h: 5 },
      available: (c) => c.isAdmin,
    });
  });
  return entries.filter((e) => e.available(ctx));
}

export function buildCatalog({ ctx, reports, queryViews }) {
  const builtins = BUILTIN_ENTRIES.filter((e) => e.available(ctx));
  return [...builtins, ...buildDynamicEntries({ reports, queryViews, ctx })];
}

let uid = 0;
export function newWidgetId(type) {
  uid += 1;
  return `${type}-${Date.now().toString(36)}-${uid}`;
}

/**
 * El dashboard de fábrica: lo que se veía antes de los widgets. Duntower no usaba la
 * actividad reciente y su calendario iba a todo el ancho.
 */
export function buildDefaultLayout({ catalog, isDuntower }) {
  const has = (type) => catalog.some((e) => e.type === type);
  const out = [];
  let y = 0;
  if (has("shortcuts")) {
    out.push({ i: "shortcuts", type: "shortcuts", x: 0, y, w: 12, h: 5, config: {} });
    y += 5;
  }
  if (isDuntower) {
    out.push({ i: "calendar", type: "calendar", x: 0, y, w: 12, h: 21, config: {} });
  } else {
    out.push({ i: "calendar", type: "calendar", x: 0, y, w: 8, h: 21, config: {} });
    out.push({ i: "recent-activity", type: "recent-activity", x: 8, y, w: 4, h: 21, config: {} });
  }
  y += 21;
  out.push({ i: "operations-summary", type: "operations-summary", x: 0, y, w: 12, h: 12, config: {} });
  return out;
}

/** Busca un lugar libre al final de la grilla para un widget nuevo. */
export function placeNewWidget(layout, w) {
  const bottom = layout.reduce((max, it) => Math.max(max, it.y + it.h), 0);
  // Si la última fila tiene hueco a la derecha, se usa; si no, va abajo.
  const lastRow = layout.filter((it) => it.y + it.h === bottom);
  const usedRight = lastRow.reduce((max, it) => Math.max(max, it.x + it.w), 0);
  if (lastRow.length && usedRight + w <= GRID_COLS) {
    const top = Math.min(...lastRow.map((it) => it.y));
    return { x: usedRight, y: top };
  }
  return { x: 0, y: bottom };
}
